import React, { useState, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Layout from 'components/shared/Layout';
import Input from 'components/shared/form/Input';
import Button from 'components/shared/form/Button';
import { NoErrors, AddError } from 'services/errorHandling/ErrorUtil';
import { nameOf } from 'services/ObjectUtil';
import { useErrors } from 'services/errorHandling/useErrors';
import { AppContext } from 'services/appContext/AppContext';
import useUserService from 'services/eva/UserService';
import { ErrorsModel } from 'services/errorHandling/ErrorModel';
import PageContent from 'components/shared/PageContent';
import Header from 'components/shared/header/Header';
import validateModel from './LandingPageModelValidator';
import { DefaultLandingPageModel, LandingPageModel } from './LandingPageModel';

export default function LandingPage(): JSX.Element {
  const history = useHistory();
  const { appContext, setAppContext } = useContext(AppContext);
  const { getErrorHandler, setErrors } = useErrors();
  const [model, setModel] = useState(DefaultLandingPageModel);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [emailIsAvailable, setEmailIsAvailable] = useState(false);
  const [showMessageEmailNotAvailable, setShowMessageEmailNotAvailable] = useState(
    false
  );
  const [submitButtonTextId, setSubmitButtonTextId] = useState(
    'landing-page.form.button.login-nextstep'
  );

  const userService = useUserService();

  const handleLoginResponse = async (
    response: EVA.Core.LoginResponse | undefined
  ) => {
    if (response && response.Authentication === 2 && response.User) {

      userService.getSubscriptions(response.User.ID).then( result => {
        console.log('subs', result);
      });
      history.push('/success');
    } else {
      const errors: ErrorsModel = {};

      AddError(
        errors,
        nameOf<LandingPageModel>('password'),
        'form.password.invalid-login'
      );

      setErrors(errors);
      setSubmitDisabled(false);
    }
  };

  const handleError = (response: any) => {
    const errors: ErrorsModel = {};

    if (response.data.Error) {
      AddError(
        errors,
        nameOf<LandingPageModel>('email'),
        response.data.Error.Message,
        false
      );
    }

    setErrors(errors);
    setSubmitDisabled(false);
  };

  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (emailIsAvailable) {
      setAppContext({ ...appContext, email: model.email });
      history.push('/register');
      return;
    }

    const validationResult = validateModel(model);
    setErrors(validationResult);

    if (NoErrors(validationResult)) {
      setSubmitDisabled(true);

      if (model.showPasswordField) {
        userService
          .login(model.email, model.password)
          .then(x => handleLoginResponse(x))
          .catch(x => handleError(x));
      } else {
        userService
          .checkEmailAddressAvailability(model.email)
          .then(isAvailable => {
            if (isAvailable) {
              setAppContext({ ...appContext, email: model.email, userToken: undefined });
              history.push('/register');
            } else {
              setSubmitDisabled(false);
              setShowMessageEmailNotAvailable(true);
              setModel({ ...model, showPasswordField: true });
              setSubmitButtonTextId('landing-page.form.button.login');
            }
          });
      }
    }
  };

  return (
    <Layout>
      <Header>
        <div className='register-title'>
          <FormattedMessage id="register.title" />
        </div>
      </Header>
      <PageContent className="landing-page">
        <form onSubmit={submitHandler}>
          <Input
            titleId="form.email.title"
            placeholderId="form.email.placeholder"
            value={model.email}
            type="email"
            autoComplete="username"
            errorHandler={getErrorHandler(nameOf<LandingPageModel>('email'))}
            onChange={(e): void => {
              setModel({
                ...model,
                email: e.target.value,
                showPasswordField: false
              });
              setAppContext({ ...appContext, email: e.target.value });
              setEmailIsAvailable(false);
              if (showMessageEmailNotAvailable) {
                setShowMessageEmailNotAvailable(false);
              }
              setSubmitButtonTextId('landing-page.form.button.login-nextstep');
            }}
          />
          {showMessageEmailNotAvailable && (
            <>
              <div className="email-notavailable">
                  <FormattedMessage id="message.email.not.available" />
              </div>
            </>
          )}
          {emailIsAvailable && (
            <div className="email-available">
              <FormattedMessage
                id="form.email.available"
                values={{ email: model.email }}
              />
            </div>
          )}
          <Button
            type="submit"
            textId={submitButtonTextId}
            disabled={submitDisabled}
          />
        </form>
      </PageContent>
    </Layout>
  );
}
